import React, { useEffect, Children, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Target, Award, Zap, Users, MessageSquare, BookOpen, ShieldCheck, Scale, Sparkles } from 'lucide-react';
import { WhatsAppCTA } from './components/WhatsAppCTA';
import { FloatingWhatsApp } from './components/FloatingWhatsApp';
import { FAQItem } from './components/FAQItem';
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
export function App() {
  // Tracking Setup
  useEffect(() => {
    // 1. Initialize Meta Pixel
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1330155108219675');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1330155108219675&ev=PageView&noscript=1" />`;
    document.head.appendChild(noscript);
    // 2. Time on Page Tracking
    const timeIntervals = [30, 60, 120];
    const timeouts = timeIntervals.map(seconds => setTimeout(() => {
      if (window.fbq) {
        window.fbq('trackCustom', 'TimeOnPage', {
          seconds
        });
      }
    }, seconds * 1000));
    // 3. Scroll Depth Tracking
    const trackedDepths = new Set<number>();
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollPosition / documentHeight * 100;
      const depths = [25, 50, 75, 100];
      depths.forEach(depth => {
        if (scrollPercentage >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          if (window.fbq) {
            window.fbq('trackCustom', 'ScrollDepth', {
              depth: `${depth}%`
            });
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      timeouts.forEach(clearTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const fadeUpChild = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut'
      }
    }
  };
  return <div className="min-h-screen bg-white font-sans text-slate-800 selection:bg-blue-100 antialiased" data-id="element-0">
      <FloatingWhatsApp data-id="element-1" />

      {/* 1. HERO SECTION */}
      <section className="relative pt-16 pb-20 px-4 bg-white overflow-hidden" data-id="element-2">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50/80 via-transparent to-transparent" data-id="element-3"></div>
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-4">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} data-id="element-5">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 font-semibold px-4 py-1.5 rounded-full text-sm mb-8 border border-blue-100/80" data-id="element-6">
              <Scale className="w-4 h-4" data-id="element-7" />
              Formación especializada para abogados
            </div>

            <h1 className="text-[2rem] md:text-5xl lg:text-[3.5rem] font-extrabold text-slate-900 mb-5 tracking-tight leading-[1.15]" data-id="element-8">
              Modernizá tu{' '}
              <span className="text-blue-600" data-id="element-9">captación jurídica</span> con{' '}
              <span className="text-blue-600" data-id="element-10">IA</span> y estrategia profesional
            </h1>

            <p className="text-lg md:text-xl text-slate-500 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-11">
              Formación práctica para ordenar tu marketing, mejorar tu captación
              y aplicar inteligencia artificial con criterio.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-10 text-sm md:text-base text-slate-600 font-medium" data-id="element-12">
              <div className="flex items-center gap-2" data-id="element-13">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-14" />
                <span data-id="element-15">Sector jurídico</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-16">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-17" />
                <span data-id="element-18">Práctico desde el día uno</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-19">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-20" />
                <span data-id="element-21">Orientación personalizada</span>
              </div>
            </div>

            <div className="max-w-sm mx-auto rounded-2xl overflow-hidden shadow border border-slate-100 bg-white opacity-70 mb-10" data-id="element-22">
              <img src="https://cdn.magicpatterns.com/uploads/57Bfua9SgcXZYP3mnTZajS/1.png" alt="Marketing Jurídico 360 con IA" className="w-full h-auto object-cover" data-id="element-23" />
            </div>

            <div className="flex flex-col items-center justify-center gap-2.5" data-id="element-24">
              <WhatsAppCTA text="Quiero saber si aplica a mi estudio" location="hero" data-id="element-25" />
              <p className="text-sm text-slate-400" data-id="element-26">
                Respuesta personalizada · Sin compromiso
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Credibility micro-strip */}
      <div className="border-y border-slate-100 bg-slate-50/50 py-4 px-4" data-id="element-27">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-slate-500 font-medium" data-id="element-28">
          <div className="flex items-center gap-2" data-id="element-29">
            <ShieldCheck className="w-4 h-4 text-blue-500" data-id="element-30" />
            <span data-id="element-31">Diseñado para el sector legal</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-32">
            <Sparkles className="w-4 h-4 text-blue-500" data-id="element-33" />
            <span data-id="element-34">+30.000 seguidores en cuenta jurídica</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-35">
            <Users className="w-4 h-4 text-blue-500" data-id="element-36" />
            <span data-id="element-37">Abogados ya lo aplican</span>
          </div>
        </div>
      </div>

      {/* 2. HOW IT WORKS */}
      <section className="py-20 px-4 bg-white" data-id="element-38">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-4xl mx-auto" data-id="element-39">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-40">
            ¿Qué pasa cuando escribís?
          </h2>

          <motion.div className="grid md:grid-cols-3 gap-6 mb-12" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-41">
            {[{
            step: '1',
            title: 'Nos escribís por WhatsApp',
            desc: 'Contanos brevemente tu situación y qué buscás.'
          }, {
            step: '2',
            title: 'Evaluamos si encaja',
            desc: 'Vemos si la formación aplica a tu perfil o estudio.'
          }, {
            step: '3',
            title: 'Te orientamos para avanzar',
            desc: 'Te explicamos el mejor camino para tu caso.'
          }].map((item, i) => <motion.div key={i} variants={fadeUpChild} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 text-center" data-id="element-42">
                <div className="w-11 h-11 bg-blue-900 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5" data-id="element-43">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-44">
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-45">{item.desc}</p>
              </motion.div>)}
          </motion.div>

          <div className="text-center space-y-2" data-id="element-46">
            <WhatsAppCTA text="Quiero ver cómo funcionaría en mi caso" location="content" data-id="element-47" />
            <p className="text-xs text-slate-400" data-id="element-48">
              Vemos si realmente aplica a tu caso
            </p>
          </div>
        </motion.div>
      </section>

      {/* 3. PROBLEM / PAIN */}
      <section className="py-20 px-4 bg-slate-50/70" data-id="element-49">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-50">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-10" data-id="element-51">
            ¿Te identificás con algo de esto?
          </h2>

          <div className="space-y-3 mb-10" data-id="element-52">
            {['Dependés del boca a boca y no tenés un sistema de captación', 'Sabés que deberías usar redes e IA pero no sabés por dónde empezar', 'Perdés consultas por falta de seguimiento profesional', 'Sentís que tu marketing no refleja la calidad de tu trabajo'].map((pain, i) => <div key={i} className="bg-white p-4 rounded-xl border border-slate-200/80 flex items-start gap-3" data-id="element-53">
                <div className="bg-red-50 p-1.5 rounded-full flex-shrink-0 mt-0.5" data-id="element-54">
                  <XCircle className="w-4 h-4 text-red-400" data-id="element-55" />
                </div>
                <p className="text-slate-700" data-id="element-56">{pain}</p>
              </div>)}
          </div>

          <div className="text-center bg-slate-900 text-white p-6 rounded-2xl mb-10" data-id="element-57">
            <p className="text-lg font-medium text-balance" data-id="element-58">
              El problema no es tu conocimiento jurídico. Es la falta de un
              sistema moderno de captación.
            </p>
          </div>

          <div className="text-center space-y-2" data-id="element-59">
            <WhatsAppCTA text="Quiero evaluar si esta formación encaja conmigo" location="pain" data-id="element-60" />
            <p className="text-xs text-slate-400" data-id="element-61">
              Recibí claridad antes de avanzar
            </p>
          </div>
        </motion.div>
      </section>

      {/* 4. WHAT IS IT */}
      <section className="py-20 px-4 bg-white" data-id="element-62">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-5xl mx-auto" data-id="element-63">
          <div className="text-center mb-12 max-w-3xl mx-auto" data-id="element-64">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-65">
              ¿Qué vas a encontrar dentro?
            </h2>
          </div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-66">
            {[{
            icon: <Target className="w-5 h-5 text-blue-600" data-id="element-67" />,
            title: 'Captación jurídica moderna',
            desc: 'Estrategia para atraer consultas calificadas'
          }, {
            icon: <Award className="w-5 h-5 text-blue-600" data-id="element-68" />,
            title: 'Posicionamiento profesional',
            desc: 'Definí tu propuesta y comunicá con autoridad'
          }, {
            icon: <Zap className="w-5 h-5 text-blue-600" data-id="element-69" />,
            title: 'IA aplicada al derecho',
            desc: 'Inteligencia artificial con criterio profesional'
          }, {
            icon: <Users className="w-5 h-5 text-blue-600" data-id="element-70" />,
            title: 'Marketing y Meta Ads',
            desc: 'Campañas simples con resultados medibles'
          }, {
            icon: <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-71" />,
            title: 'Seguimiento y conversión',
            desc: 'Filtrá, respondé y convertí consultas en clientes'
          }, {
            icon: <BookOpen className="w-5 h-5 text-blue-600" data-id="element-72" />,
            title: 'Implementación guiada',
            desc: 'Hoja de ruta clara para aplicar en 30 días'
          }].map((item, i) => <motion.div key={i} variants={fadeUpChild} className="bg-slate-50 p-5 rounded-xl border border-slate-100 hover:border-blue-200 transition-colors" data-id="element-73">
                <div className="bg-white w-10 h-10 rounded-lg flex items-center justify-center shadow-sm border border-slate-100 mb-3" data-id="element-74">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-75">
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-76">{item.desc}</p>
              </motion.div>)}
          </motion.div>

          <div className="text-center mt-12 space-y-2" data-id="element-77">
            <WhatsAppCTA text="Quiero recibir orientación por WhatsApp" location="content" data-id="element-78" />
            <p className="text-xs text-slate-400" data-id="element-79">
              Te orientamos sobre lo que mejor aplica a tu caso
            </p>
          </div>
        </motion.div>
      </section>

      {/* 5. FILTER */}
      <section className="py-20 px-4 bg-slate-50/70" data-id="element-80">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-4xl mx-auto" data-id="element-81">
          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-82">
            <div className="bg-white rounded-2xl p-7 border-l-4 border-l-emerald-500 shadow-sm" data-id="element-83">
              <h3 className="text-xl font-bold text-slate-900 mb-5" data-id="element-84">
                Es para vos si...
              </h3>
              <ul className="space-y-3.5" data-id="element-85">
                {['Querés profesionalizar tu captación de consultas', 'Querés dejar de depender solo de referidos', 'Querés aplicar IA con criterio y sin improvisar', 'Querés modernizar cómo atraés y seguís consultas', 'Querés ordenar el crecimiento de tu práctica'].map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-86">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-87" />
                    <span className="text-slate-700 text-sm font-medium" data-id="element-88">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-7 border-l-4 border-l-slate-200 shadow-sm" data-id="element-89">
              <h3 className="text-xl font-bold text-slate-900 mb-5" data-id="element-90">
                No es para vos si...
              </h3>
              <ul className="space-y-3.5" data-id="element-91">
                {['Buscás atajos mágicos sin aplicar nada', 'No querés modernizar tu forma de trabajar', 'Buscás solo teoría sin implementación', 'No estás dispuesto a incorporar procesos claros'].map((item, i) => <li key={i} className="flex items-start gap-2.5" data-id="element-92">
                    <XCircle className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" data-id="element-93" />
                    <span className="text-slate-500 text-sm font-medium" data-id="element-94">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="text-center space-y-2" data-id="element-95">
            <WhatsAppCTA text="Quiero ver si esto sirve para mi estudio" location="mid" data-id="element-96" />
            <p className="text-xs text-slate-400" data-id="element-97">
              Vemos juntos si encaja con tu situación
            </p>
          </div>
        </motion.div>
      </section>

      {/* 6. AUTHORITY & TRUST */}
      <section className="py-20 px-4 bg-white" data-id="element-98">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-4xl mx-auto" data-id="element-99">
          <div className="text-center mb-10 max-w-2xl mx-auto" data-id="element-100">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-id="element-101">
              No es marketing genérico. Está pensado para abogados.
            </h2>
            <p className="text-slate-500 leading-relaxed" data-id="element-102">
              Cada módulo, herramienta y estrategia fue creada desde experiencia
              directa con abogados y despachos, para la realidad del ejercicio
              jurídico en Argentina.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-14" data-id="element-103">
            {['Especialización exclusiva en el sector legal', '+30.000 seguidores en cuenta jurídica real', 'Metodología probada con abogados en actividad'].map((item, i) => <div key={i} className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-3" data-id="element-104">
                <ShieldCheck className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-105" />
                <span className="font-medium text-slate-700 text-sm" data-id="element-106">
                  {item}
                </span>
              </div>)}
          </div>

          <motion.div className="grid md:grid-cols-3 gap-5 mb-12" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-107">
            {[{
            name: 'Martín L.',
            spec: 'Derecho Laboral',
            text: 'Pasé de depender del boca a boca a tener consultas semanales. La IA me ahorra horas de trabajo.'
          }, {
            name: 'Carolina R.',
            spec: 'Familia',
            text: 'Recién matriculada y perdida. Este curso me dio el paso a paso exacto para cerrar mis primeros clientes.'
          }, {
            name: 'Diego F.',
            spec: 'Derecho Comercial',
            text: 'El sistema de pre-calificación vale 10 veces lo que cuesta. Dejé de perder tiempo con consultas vacías.'
          }].map((t, i) => <motion.div key={i} variants={fadeUpChild} className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-108">
                <p className="text-slate-600 italic mb-5 leading-relaxed text-sm" data-id="element-109">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3" data-id="element-110">
                  <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-900 font-bold text-sm" data-id="element-111">
                    {t.name.charAt(0)}
                  </div>
                  <div data-id="element-112">
                    <p className="font-bold text-slate-900 text-sm" data-id="element-113">{t.name}</p>
                    <p className="text-xs text-slate-400" data-id="element-114">{t.spec}</p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          <div className="text-center space-y-2" data-id="element-115">
            <WhatsAppCTA text="Quiero recibir orientación personalizada" location="content" data-id="element-116" />
            <p className="text-xs text-slate-400" data-id="element-117">
              Respuesta personalizada para tu situación
            </p>
          </div>
        </motion.div>
      </section>

      {/* 7. FAQ */}
      <section className="py-20 px-4 bg-slate-50/70" data-id="element-118">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-119">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-10" data-id="element-120">
            Preguntas frecuentes
          </h2>

          <div className="space-y-3 mb-12" data-id="element-121">
            {[{
            q: '¿Esto aplica si trabajo solo?',
            a: 'Sí. Está pensado tanto para abogados independientes como para despachos.'
          }, {
            q: '¿Necesito experiencia previa en IA o marketing?',
            a: 'No. Se trabaja desde cero, paso a paso.'
          }, {
            q: '¿Puedo aplicarlo si hoy no tengo ningún sistema?',
            a: 'Sí. El curso te guía para armar uno desde el inicio.'
          }, {
            q: '¿Se adapta a distintas áreas del derecho?',
            a: 'Sí. Los principios aplican a cualquier especialidad jurídica.'
          }, {
            q: '¿Cómo sé si encaja con mi situación?',
            a: 'Escribinos por WhatsApp y te orientamos en minutos.'
          }, {
            q: '¿Qué pasa después de escribir por WhatsApp?',
            a: 'Te respondemos personalmente, evaluamos tu caso y te explicamos cómo avanzar.'
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-122" />)}
          </div>

          <div className="text-center space-y-2" data-id="element-123">
            <WhatsAppCTA text="Quiero resolver mi situación puntual" location="faq" data-id="element-124" />
            <p className="text-xs text-slate-400" data-id="element-125">
              Te orientamos por WhatsApp sin compromiso
            </p>
          </div>
        </motion.div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="py-20 px-4 bg-slate-900 text-white text-center relative overflow-hidden" data-id="element-126">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-transparent to-transparent opacity-60" data-id="element-127"></div>
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} className="max-w-2xl mx-auto relative z-10" data-id="element-128">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-5 leading-tight text-balance" data-id="element-129">
            ¿Querés ver si esta formación encaja con tu perfil o tu estudio?
          </h2>

          <p className="text-base md:text-lg text-slate-300 mb-10 leading-relaxed text-balance" data-id="element-130">
            Escribinos por WhatsApp y te orientamos para que entiendas si esto
            aplica a tu caso y cuál es la mejor forma de avanzar.
          </p>

          <div className="flex flex-col items-center justify-center gap-3" data-id="element-131">
            <WhatsAppCTA text="Hablar por WhatsApp ahora" location="final" className="shadow-2xl" data-id="element-132" />
            <p className="text-sm text-slate-400" data-id="element-133">
              Te respondemos personalmente.
            </p>
          </div>
        </motion.div>
      </section>

      {/* 9. FOOTER */}
      <footer className="py-8 px-4 bg-slate-950 text-slate-500 text-center text-sm" data-id="element-134">
        <div className="max-w-4xl mx-auto" data-id="element-135">
          <p className="font-semibold text-slate-400 mb-1" data-id="element-136">
            Marketing Jurídico 360 con IA
          </p>
          <p className="text-xs" data-id="element-137">
            © {new Date().getFullYear()} Todos los derechos reservados.
          </p>
          <p className="mt-3 text-xs max-w-xl mx-auto opacity-50" data-id="element-138">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}